/**
 * Hauptkomponente der Anwendung
 *
 * Verwaltet Routing, Authentifizierung und Route-Guards.
 * Seiten per lazy + Prefetch nach Login (Menüwechsel ohne Nachlade-Ruckler).
 *
 * @module App
 */

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { focusHandling } from 'cruip-js-toolkit';
import ErrorNotification from './components/ErrorNotification';
import ErrorBoundary from './components/ErrorBoundary';
import AuthLoading from './components/AuthLoading';
import { getApiBaseUrl } from './utils/apiBaseUrl';

const loadDashboard = () => import('./pages/Dashboard');
const loadCompany = () => import('./pages/Settings/Company');
const loadUser = () => import('./pages/Settings/User');
const loadEmployees = () => import('./pages/Settings/Employee');
const loadParking = () => import('./pages/Parking');
const loadColors = () => import('./pages/Colors');
const loadBrightness = () => import('./pages/Brightness');
const loadPanelGroups = () => import('./pages/PanelGroups');
const loadPanels = () => import('./pages/Panels');
const loadCalculates = () => import('./pages/Calculates');
const loadPanelsOpen = () => import('./pages/PanelsOpen');
const loadPanelsStatus = () => import('./pages/PanelsStatus');
const loadDocumentation = () => import('./pages/Documentation');
const loadPaloinoManager = () => import('./pages/PaloinoManager');
const loadSoftwareVersions = () => import('./pages/SoftwareVersions');
const loadMailSettings = () => import('./pages/Settings/Mail');
const loadWebsiteSettings = () => import('./pages/Settings/Website');
const loadPublicStatus = () => import('./pages/PublicStatus');
const loadPublicMap = () => import('./pages/PublicMap');
const loadPublicList = () => import('./pages/PublicList');
const loadAuthLogin = () => import('./pages/auth/Login');
const loadAuthLogout = () => import('./pages/auth/Logout');
const loadAuthVerify = () => import('./pages/auth/Verify');
const loadAuthSignup = () => import('./pages/auth/Signup');
const loadAuthSetup = () => import('./pages/auth/Setup');
const loadAuthForgot = () => import('./pages/auth/ForgotPassword');
const loadAuthReset = () => import('./pages/auth/ResetPassword');

const Dashboard = lazy(loadDashboard);
const Company = lazy(loadCompany);
const User = lazy(loadUser);
const Employees = lazy(loadEmployees);
const Parking = lazy(loadParking);
const Colors = lazy(loadColors);
const Brightness = lazy(loadBrightness);
const PanelGroups = lazy(loadPanelGroups);
const Panels = lazy(loadPanels);
const Calculates = lazy(loadCalculates);
const PanelsOpen = lazy(loadPanelsOpen);
const PanelsStatus = lazy(loadPanelsStatus);
const PublicStatus = lazy(loadPublicStatus);
const PublicMap = lazy(loadPublicMap);
const PublicList = lazy(loadPublicList);
const AuthLogin = lazy(loadAuthLogin);
const AuthLogout = lazy(loadAuthLogout);
const AuthVerify = lazy(loadAuthVerify);
const AuthSignup = lazy(loadAuthSignup);
const Documentation = lazy(loadDocumentation);
const PaloinoManagerPage = lazy(loadPaloinoManager);
const SoftwareVersionsPage = lazy(loadSoftwareVersions);
const MailSettingsPage = lazy(loadMailSettings);
const WebsiteSettingsPage = lazy(loadWebsiteSettings);
const AuthSetup = lazy(loadAuthSetup);
const AuthForgot = lazy(loadAuthForgot);
const AuthReset = lazy(loadAuthReset);

/** Hauptmenü-Seiten nach Login im Hintergrund laden */
const PREFETCH_WHEN_AUTHED = [
  loadDashboard,
  loadPanels,
  loadPanelsStatus,
  loadParking,
  loadPanelGroups,
  loadBrightness,
  loadCalculates,
  loadColors,
  loadUser,
  loadEmployees,
  loadCompany,
  loadDocumentation,
  loadPaloinoManager,
  loadSoftwareVersions,
  loadMailSettings,
  loadWebsiteSettings,
  loadPanelsOpen,
];

function prefetchRoutes(loaders) {
  loaders.forEach((load) => {
    try {
      load();
    } catch (e) {
      // Prefetch-Fehler ignorieren
    }
  });
}

function schedulePrefetch(loaders) {
  const run = () => prefetchRoutes(loaders);
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 2500 });
  } else {
    setTimeout(run, 400);
  }
}

function App() {
  const location = useLocation();
  const [isAuth, setIsAuth] = useState(false);
  const [authLoaded, setAuthLoaded] = useState(false);

  useEffect(() => {
    document.querySelector('html').style.scrollBehavior = 'auto';
    window.scroll({ top: 0 });
    document.querySelector('html').style.scrollBehavior = '';
    focusHandling('outline');
    getAuth();
  }, [location.pathname]);

  useEffect(() => {
    if (authLoaded && isAuth) {
      schedulePrefetch(PREFETCH_WHEN_AUTHED);
    }
  }, [authLoaded, isAuth]);

  const getAuth = async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}v2/authentication/status/`, {
        credentials: 'include',
      });
      const text = await res.text();
      if (!text || text.trim() === '') {
        setIsAuth(false);
        setAuthLoaded(true);
        return;
      }
      const data = JSON.parse(text);
      setIsAuth(data.success === true);
    } catch (err) {
      setIsAuth(false);
    } finally {
      setAuthLoaded(true);
    }
  };

  return (
    <ErrorBoundary>
      <ErrorNotification />
      <Suspense fallback={<AuthLoading label="Seite wird geladen…" />}>
        <Switch>
          <PrivateRoute exact path="/" component={Dashboard} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/panels/status" component={PanelsStatus} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/panels/:id" component={PanelsOpen} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/panels" component={Panels} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/colors" component={Colors} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/brightness" component={Brightness} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/panel-groups" component={PanelGroups} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/parking" component={Parking} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/calculates" component={Calculates} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/auth/logout" component={AuthLogout} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/company" component={Company} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/employees" component={Employees} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/user" component={User} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/dokumentation" component={Documentation} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/paloino-manager" component={PaloinoManagerPage} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/software-versions" component={SoftwareVersionsPage} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/settings/mail" component={MailSettingsPage} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/settings/website" component={WebsiteSettingsPage} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/hilfe" component={Documentation} authLoaded={authLoaded} isAuth={isAuth} />
          <PrivateRoute exact path="/downloads" component={PaloinoManagerPage} authLoaded={authLoaded} isAuth={isAuth} />

          <Route exact path="/public/status" component={PublicStatus} />
          <Route exact path="/public/map" component={PublicMap} />
          <Route exact path="/public/list" component={PublicList} />

          <PublicRoute path="/auth/login" authLoaded={authLoaded} isAuth={isAuth}>
            <AuthLogin />
          </PublicRoute>
          <PublicRoute path="/auth/setup" authLoaded={authLoaded} isAuth={isAuth}>
            <AuthSetup />
          </PublicRoute>
          <PublicRoute path="/auth/forgot-password" authLoaded={authLoaded} isAuth={isAuth}>
            <AuthForgot />
          </PublicRoute>
          <PublicRoute path="/auth/reset-password" authLoaded={authLoaded} isAuth={isAuth}>
            <AuthReset />
          </PublicRoute>
          <PublicRoute path="/auth/register" authLoaded={authLoaded} isAuth={isAuth}>
            <AuthSignup />
          </PublicRoute>
          <PublicRoute path="/auth/verify" authLoaded={authLoaded} isAuth={isAuth}>
            <AuthVerify />
          </PublicRoute>

          <ErrorRoute path="*" authLoaded={authLoaded} isAuth={isAuth} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

function PrivateRoute({ ...rest }) {
  if (rest.authLoaded !== true) {
    return <AuthLoading label="Anmeldung prüfen…" />;
  }
  if (!rest.isAuth) {
    return <Redirect to="/auth/login" />;
  }
  return <Route {...rest} />;
}

function PublicRoute({ children, ...rest }) {
  if (rest.authLoaded !== true) {
    return <AuthLoading label="Anmeldung prüfen…" />;
  }
  return (
    <Route
      {...rest}
      render={() =>
        rest.isAuth ? <Redirect to="/" /> : children
      }
    />
  );
}

function ErrorRoute({ ...rest }) {
  if (rest.authLoaded !== true) {
    return <AuthLoading />;
  }
  return (
    <Route
      {...rest}
      render={() =>
        rest.isAuth ? <Redirect to="/" /> : <Redirect to="/auth/login" />
      }
    />
  );
}

export default App;
